import React, { useEffect, useMemo, useState } from "react";
import {
    computeFuturesLeverageStats,
    getMaxQuantityAtLeverage,
} from "./futuresLeverageHelpers";
import FuturesLeverageSlider from "./FuturesLeverageSlider";

/**
 * Futures — “Adjust Leverage” popup.
 * onConfirm(leverage) may return a Promise; resolves truthy → close.
 */
export default function MarginLeverageModal({
    open,
    onClose,
    onConfirm,
    onOpenBatchAdjust,
    batchAdjustActive = false,
    batchAdjustTrail = "Off",
    coinLabel,
    coinIconSrc,
    leverage,
    onChangeLeverage,
    maxLeverage = 125,
    leverageTiers = [],
    availableBalance = 0,
    markPrice = null,
    baseSymbol = "BTC",
    quoteSymbol = "USDT",
    formatTotal,
}) {
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (!open) {
            setSubmitting(false);
            return;
        }
        const onKey = (e) => {
            if (e.key === "Escape" && !submitting) onClose?.();
        };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [open, onClose, submitting]);

    const fmt = (n, dp = 2) => {
        if (formatTotal) return formatTotal(n);
        const x = Number(n);
        if (!Number.isFinite(x)) return "0";
        return x.toLocaleString("en-US", { maximumFractionDigits: dp, minimumFractionDigits: 0 });
    };

    const clamp = (n) => {
        const x = Number(n);
        if (!Number.isFinite(x) || x < 1) return 1;
        return Math.min(Math.round(x), maxLeverage);
    };

    const stats = useMemo(
        () =>
            computeFuturesLeverageStats({
                availableBalance,
                leverage,
                maxLeverage,
                leverageTiers,
            }),
        [availableBalance, leverage, maxLeverage, leverageTiers]
    );

    const priceNum = Number(markPrice);
    const maxQty = getMaxQuantityAtLeverage(leverageTiers, leverage, priceNum);
    const maxNotional = stats.maxNotionalAtLev;

    if (!open) return null;

    const maxPosLabel = (() => {
        if (Number.isFinite(maxNotional) && maxNotional === Infinity) {
            return "No cap";
        }
        if (Number.isFinite(priceNum) && priceNum > 0 && maxQty != null) {
            return `${fmt(maxQty, 8)} ${baseSymbol} (≈ ${fmt(maxNotional)} ${quoteSymbol})`;
        }
        if (Number.isFinite(maxNotional) && maxNotional !== Infinity) {
            return `${fmt(maxNotional)} ${quoteSymbol}`;
        }
        return "—";
    })();

    const handleConfirm = async () => {
        if (submitting) return;
        const next = clamp(leverage);
        setSubmitting(true);
        try {
            const ok = await onConfirm?.(next);
            if (ok !== false) onClose?.();
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="agce-margin-lvg-modal-root" role="presentation">
            <div
                className="agce-margin-lvg-modal-backdrop"
                onClick={() => {
                    if (!submitting) onClose?.();
                }}
                aria-hidden
            />
            <div
                className="agce-margin-lvg-modal-panel"
                role="dialog"
                aria-modal="true"
                aria-labelledby="agce-margin-lvg-title"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="agce-margin-lvg-modal-head">
                    <h2 id="agce-margin-lvg-title" className="agce-margin-lvg-modal-title">
                        Adjust Leverage
                    </h2>
                    <button
                        type="button"
                        className="agce-margin-lvg-modal-close"
                        onClick={onClose}
                        aria-label="Close"
                        disabled={submitting}
                    >
                        <i className="ri-close-line ri-lg" aria-hidden />
                    </button>
                </div>
                <div className="agce-margin-lvg-modal-body">
                    <span className="agce-margin-lvg-field-label">Pair</span>
                    <div className="agce-margin-lvg-coin-row">
                        {coinIconSrc ? <img src={coinIconSrc} alt="" width={24} height={24} /> : null}
                        <span>{coinLabel || "—/—"}</span>
                    </div>

                    <span className="agce-margin-lvg-field-label">Leverage</span>
                    <FuturesLeverageSlider
                        value={leverage}
                        onChange={onChangeLeverage}
                        maxLeverage={maxLeverage}
                    />

                    <ul className="agce-margin-lvg-notes">
                        <li>Maximum position at current leverage: {maxPosLabel}</li>
                        <li>
                            Please note that leverage changing will also apply for open positions and open
                            orders.
                        </li>
                        <li>
                            Selecting higher leverage {/* such as [{clamp(leverage)}x] */} increases your
                            liquidation risk. Always manage your risk levels.
                        </li>
                    </ul>

                    {availableBalance <= 0 && (
                        <p className="agce-margin-lvg-warn">
                            The current available margin ≤ 0. You can increase the leverage or add margin.
                        </p>
                    )}

                    {typeof onOpenBatchAdjust === "function" ? (
                        <button
                            type="button"
                            className="agce-ft-mm-defaults agce-margin-lvg-defaults"
                            onClick={() => onOpenBatchAdjust()}
                            disabled={submitting}
                        >
                            <span>Default leverage &amp; margin mode</span>
                            <span className="agce-ft-mm-defaults__trail">
                                {batchAdjustTrail || (batchAdjustActive ? "On" : "Off")}{" "}
                                <span aria-hidden>›</span>
                            </span>
                        </button>
                    ) : null}
                </div>
                <div className="agce-margin-lvg-modal-foot">
                    <button
                        type="button"
                        className="agce-margin-lvg-btn agce-margin-lvg-btn--ghost"
                        onClick={onClose}
                        disabled={submitting}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        className="agce-margin-lvg-btn agce-margin-lvg-btn--primary"
                        disabled={submitting}
                        onClick={handleConfirm}
                    >
                        {submitting ? "Saving…" : "Confirm"}
                    </button>
                </div>
            </div>
        </div>
    );
}
